import React, { useState, useEffect, useMemo } from 'react';
import { fetchPrediction, getRecommendedSoil } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useGeolocation } from '../hooks/useGeolocation';
import { 
  CloudSun, 
  MapPin, 
  Droplets, 
  Wind,
  Tractor,
  Sprout,
  CheckCircle2,
  AlertCircle,
  Target,
  ChevronRight,
  TrendingUp,
  Leaf,
  BookOpen,
  Map as MapIcon,
  Loader2
} from 'lucide-react';

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { location, loading: geoLoading, error: geoError } = useGeolocation();
  const [city, setCity] = useState("Kolkata");
  const [soil, setSoil] = useState("Alluvial");
  const [recommendedSoil, setRecommendedSoil] = useState("Alluvial");
  
  const [loading, setLoading] = useState(true);
  const [soilLoading, setSoilLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const cropHealth = data?.crop_health || null;
  const healthScore = cropHealth?.score ?? cropHealth?.health_score ?? null;
  const healthTone = useMemo(() => {
    if (healthScore === null || healthScore === undefined) {
      return { color: '#10b981', label: t('dashboard.main_grid.optimal') };
    }
    if (healthScore >= 70) return { color: '#10b981', label: 'Strong' };
    if (healthScore >= 40) return { color: '#f59e0b', label: 'Caution' };
    return { color: '#ef4444', label: 'At Risk' };
  }, [healthScore, t]);

  // Auto-fetch location from GPS and update city
  useEffect(() => {
    if (location?.city && location.city !== 'Unknown') {
      setCity(location.city);
    }
  }, [location]);

  // Fetch recommended soil when city changes
  useEffect(() => {
    if (city) {
      const fetchSoil = async () => {
        setSoilLoading(true);
        try {
          const result = await getRecommendedSoil(city);
          const suggested = result.recommended_soil || "Alluvial";
          setRecommendedSoil(suggested);
          // Auto-fill soil if it hasn't been manually changed
          setSoil(suggested);
          console.log(`Auto-detected soil for ${city}: ${suggested}`);
        } catch (err) {
          console.error("Error fetching soil:", err);
          setRecommendedSoil("Alluvial");
        } finally {
          setSoilLoading(false);
        }
      };
      fetchSoil();
    }
  }, [city]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Pass GPS coordinates if available, otherwise use city name
      const result = await fetchPrediction(
        city,
        soil,
        location?.latitude || null,
        location?.longitude || null,
        i18n.language
      );
      setData(result);
      if (result) {
        localStorage.setItem('farmCity', result.location || city);
        localStorage.setItem('farmSoil', soil);
        if (result.predicted_crop) {
          localStorage.setItem('farmCrop', result.predicted_crop);
        }
        if (result.crop_health) {
          localStorage.setItem('cropHealth', JSON.stringify(result.crop_health));
        }
      }
    } catch (err) {
      setError("Connecting to backend failed. To get real AI data, run: uvicorn app.main:app");
      setData({
        location: city,
        weather: { temperature: 28, humidity: 72, rainfall: 4 },
        predicted_crop: "Rice",
        soil_condition: "Wet",
        irrigation: "No",
        crop_health: {
          score: 65,
          disease_risk: "Moderate",
          disease_name: "Blast fungus",
          pest_alert: false,
          pest_name: "None",
          nutrient_deficiency: "Zinc",
          nutrient_tip: "Apply zinc sulfate before sowing"
        },
        ai_recommendation: "Hold off on watering today since moisture sits high (72%). Ensure the field has appropriate drainage; your rice crop is growing optimally."
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [location?.latitude, location?.longitude, i18n.language]); // Also trigger a load when GPS or Language stabilizes

  const handleApplyParams = (e) => {
    e.preventDefault();
    loadData();
  };

  if (loading && !data) {
    return (
      <div className="h-[70vh] flex flex-col items-center justify-center space-y-4">
        <Sprout className="text-emerald-600 dark:text-emerald-400 animate-spin" size={48} />
        <p className="text-neutral-600 dark:text-neutral-400 font-medium">{t('dashboard.fetching')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      
      {/* 1. HERO BANNER */}
      <div className="relative w-full rounded-2xl overflow-visible shadow-sm h-auto md:h-56 flex flex-col justify-between z-20">
        {/* Farm Background Image with solid Greenery overlay */}
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?q=80&w=1740&auto=format&fit=crop')] bg-cover bg-center rounded-2xl overflow-hidden"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 via-emerald-800/80 to-transparent rounded-2xl overflow-hidden"></div>
        
        {/* Banner Content */}
        <div className="relative z-10 p-6 md:p-8 flex flex-col md:flex-row md:items-end justify-between h-full">
          <div className="mb-6 md:mb-0">
            <div className="inline-flex items-center gap-1.5 bg-white/20 text-emerald-50 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider backdrop-blur-sm mb-3">
              <Sprout size={14} /> {t('dashboard.hero.tag')}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white drop-shadow-md">{t('dashboard.hero.heading')}</h1>
            <p className="text-emerald-100 text-sm md:text-base mt-2 font-medium max-w-lg">
              {t('dashboard.hero.desc')}
            </p>
          </div>
          
          {/* Quick Location Settings inside Banner */}
          <form onSubmit={handleApplyParams} className="bg-white p-2 rounded-xl flex items-center gap-2 shadow-lg w-full md:w-auto overflow-visible relative">
            
            <div className="flex items-center gap-2 px-3 py-2 bg-neutral-50 rounded-lg whitespace-nowrap">
               <div className="relative flex items-center">
                 {geoLoading && <Loader2 className="absolute -left-6 animate-spin text-emerald-600" size={14} />}
                 <MapPin size={16} className={geoError ? 'text-red-500' : 'text-emerald-600'} />
               </div>
               <input 
                 type="text" 
                 value={city}
                 onChange={(e) => setCity(e.target.value)}
                 className="bg-transparent border-none text-neutral-800 font-bold focus:ring-0 w-20 md:w-24 text-sm p-0 placeholder-neutral-400"
                 placeholder={t('dashboard.hero.city_placeholder')}
                 title={geoError ? `${t('dashboard.hero.gps_error')}: ${geoError}` : location ? `${t('dashboard.hero.gps_detected')}: ${location.fullAddress}` : t('dashboard.hero.enable_gps')}
               />
            </div>

            <div className="relative group">
              <div className="flex items-center gap-2 px-3 py-2 bg-neutral-50 rounded-lg whitespace-nowrap overflow-visible">
                 {soilLoading && <Loader2 className="animate-spin text-emerald-600" size={14} />}
                 {!soilLoading && <MapIcon size={16} className="text-emerald-600" />}
                 <select 
                   value={soil}
                   onChange={(e) => setSoil(e.target.value)}
                   className="bg-transparent border-none text-neutral-800 font-bold focus:ring-0 text-sm p-0 pr-6"
                   title={`Detected: ${recommendedSoil} (click to change)`}
                 >
                   <option value="Alluvial">Alluvial</option>
                   <option value="Laterite">Laterite</option>
                   <option value="Black">Black</option>
                   <option value="Red">Red</option>
                   <option value="Terai">Terai</option>
                 </select>
              </div>
              <div className="absolute top-full left-0 mt-2 hidden group-hover:block bg-slate-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50">
                ðŸ“ {t('dashboard.hero.auto_detected')}: <strong>{recommendedSoil}</strong>
              </div>
            </div>

            <button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg text-sm font-bold transition-colors ml-1">
              {t('dashboard.hero.apply_btn')}
            </button>
          </form>
        </div>
      </div>


      {geoError && !location && (
        <div className="bg-blue-50 text-blue-800 p-4 rounded-xl text-sm border border-blue-200 flex items-center gap-2 font-medium dark:bg-blue-900/20 dark:border-blue-900/50 dark:text-blue-300">
          <AlertCircle size={18} />
          {t('dashboard.errors.gps_access')}: {geoError}. {t('dashboard.errors.manual_select')}
        </div>
      )}

      {/* 2. CORE METRICS ROW */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        
        {/* Weather Card */}
        <div className="dashboard-card p-6 border-t-4 border-t-emerald-500">
          <div className="flex items-center justify-between mb-4">
             <h3 className="text-neutral-800 dark:text-neutral-100 font-bold">{t('dashboard.weather.title')}</h3>
             <div className="px-2 py-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-md text-xs font-bold border border-emerald-100 dark:border-emerald-800">
               {data?.location ?? city}
             </div>
          </div>
          
          <div className="flex items-center justify-between my-2">
             <div className="flex items-baseline gap-1">
               <span className="text-5xl font-black text-neutral-900 dark:text-white">{data?.weather?.temperature ?? '--'}&deg;</span>
             </div>
             <CloudSun size={48} className="text-amber-500" strokeWidth={1.5} />
          </div>

          <div className="mt-6 flex items-center gap-6 text-sm text-neutral-600 dark:text-neutral-400 font-medium">
             <div className="flex items-center gap-2 bg-neutral-50 dark:bg-slate-700/50 px-3 py-1.5 rounded-lg w-full">
               <Droplets size={16} className="text-blue-500"/> 
               {data?.weather?.humidity ?? '--'}% {t('dashboard.weather.hum')}
             </div>
             <div className="flex items-center gap-2 bg-neutral-50 dark:bg-slate-700/50 px-3 py-1.5 rounded-lg w-full">
               <Wind size={16} className="text-emerald-500"/> 
               {data?.weather?.rainfall ?? '--'}mm {t('dashboard.weather.rain')}
             </div>
          </div>
        </div>

        {/* Soil Health Action */}
        <div className="dashboard-card p-6 border-t-4 border-t-blue-500 flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
             <h3 className="text-neutral-800 dark:text-neutral-100 font-bold">{t('dashboard.soil.title')}</h3>
             <Target className="text-blue-500" size={20} />
          </div>
          
          <div className="space-y-3 mt-auto">
            {/* Condition Row */}
            <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-800/30">
               <div className="flex items-center gap-3">
                 <div className="p-2 bg-white dark:bg-slate-800 shadow-sm border border-blue-100 dark:border-blue-800 rounded-lg text-blue-600 dark:text-blue-400">
                   <Leaf size={18} />
                 </div>
                 <div>
                   <p className="text-xs text-blue-600 dark:text-blue-400 font-bold uppercase tracking-wider mb-0.5">{t('dashboard.soil.moisture_level')}</p>
                   <p className="text-lg font-black text-blue-900 dark:text-blue-300">{data?.soil_condition ?? '--'}</p>
                 </div>
               </div>
            </div>

            {/* Irrigation Row */}
            <div className={`flex items-center justify-between p-3 rounded-xl border ${data?.irrigation === 'Yes' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/30' : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/30'}`}>
               <div className="flex items-center gap-3">
                 <div className={`p-2 bg-white dark:bg-slate-800 shadow-sm border rounded-lg ${data?.irrigation === 'Yes' ? 'border-amber-100 text-amber-600' : 'border-emerald-100 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400'}`}>
                   <CheckCircle2 size={18} />
                 </div>
                 <div>
                   <p className={`text-xs font-bold uppercase tracking-wider mb-0.5 ${data?.irrigation === 'Yes' ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400'}`}>{t('dashboard.soil.recommended_action')}</p>
                   <p className={`text-lg font-black ${data?.irrigation === 'Yes' ? 'text-amber-900 dark:text-amber-300' : 'text-emerald-900 dark:text-emerald-300'}`}>
                     {data?.irrigation === 'Yes' ? t('dashboard.soil.irrigate_yes') : (data?.irrigation === 'No' ? t('dashboard.soil.irrigate_no') : t('dashboard.soil.irrigate_monitor'))}
                   </p>
                 </div>
               </div>
            </div>
          </div>
        </div>

        {/* AI Recommendation Spotlight */}
        <div className="dashboard-card p-6 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50 flex flex-col w-full md:col-span-2 lg:col-span-1 shadow-inner">
           <div className="flex items-center gap-2 mb-3">
             <span className="p-1.5 bg-emerald-200 dark:bg-emerald-800 rounded-lg text-emerald-700 dark:text-emerald-300"><Sprout size={16} /></span>
             <h3 className="font-bold text-emerald-900 dark:text-emerald-100">{t('dashboard.ai.title')}</h3>
           </div>
           
           <div className="flex-1 my-2 bg-white dark:bg-slate-800 p-4 rounded-xl border border-emerald-100 dark:border-emerald-800/50 relative shadow-sm">
             <div className="absolute top-4 -left-2 w-4 h-4 bg-white dark:bg-slate-800 border-l border-b border-emerald-100 dark:border-emerald-800/50 rotate-45"></div>
             <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200 leading-relaxed relative z-10 line-clamp-4">
               {data?.ai_recommendation || t('dashboard.ai.fallback')}
             </p>
           </div>
           
           <button 
             onClick={() => window.location.href = '/dashboard/ai'}
             className="mt-4 w-full py-3 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600 text-white rounded-xl text-sm font-bold transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
           >
             {t('dashboard.ai.button')} <ChevronRight size={16}/>
           </button>
        </div>
      </div>

      {/* 2.5 Crop Insights */}
      <div className="dashboard-card p-6 border border-emerald-100 dark:border-emerald-900/40 bg-white dark:bg-slate-800">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex items-center gap-6">
            <div
              className="relative w-28 h-28 rounded-full p-2"
              style={{
                background: `conic-gradient(${healthTone.color} ${(healthScore ?? 0) * 3.6}deg, #e5e7eb 0deg)`
              }}
            >
              <div className="absolute inset-2 rounded-full bg-white dark:bg-slate-900 flex flex-col items-center justify-center">
                <span className="text-2xl font-black text-neutral-900 dark:text-white">{healthScore ?? '--'}</span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">Health</span>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-black text-neutral-900 dark:text-neutral-100">Crop Insights</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">{data?.predicted_crop || 'Crop'} health outlook</p>
              <span className="inline-flex mt-3 items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                {healthTone.label}
              </span>
            </div>
          </div>

          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl border border-neutral-200 dark:border-slate-700 bg-neutral-50 dark:bg-slate-700/30">
              <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Disease Risk</p>
              <p className="text-lg font-black text-neutral-800 dark:text-neutral-100 mt-2">
                {cropHealth?.disease_risk || '--'}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                {cropHealth?.disease_name || 'None detected'}
              </p>
            </div>
            <div className="p-4 rounded-xl border border-neutral-200 dark:border-slate-700 bg-neutral-50 dark:bg-slate-700/30">
              <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Nutrient Gap</p>
              <p className="text-lg font-black text-neutral-800 dark:text-neutral-100 mt-2">
                {cropHealth?.nutrient_deficiency || '--'}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                {cropHealth?.nutrient_tip || 'Run a soil test to confirm.'}
              </p>
            </div>
          </div>
        </div>

        <div className={`mt-5 rounded-xl p-4 border ${cropHealth?.pest_alert ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/30' : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/30'}`}>
          <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">
            {cropHealth?.pest_alert ? `Pest Alert: ${cropHealth?.pest_name || 'Risk detected'}` : 'No active pest alert'}
          </p>
          <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
            {cropHealth?.pest_alert
              ? 'Increase field scouting and prepare preventive control measures.'
              : 'Maintain weekly checks and keep the field clean.'}
          </p>
        </div>
      </div>

      {/* 3. MAIN CONTENT GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-2">
        
        {/* Recommended Crop Deep Dive */}
        <div className="lg:col-span-2 dashboard-card border-none overflow-hidden flex flex-col shadow-md">
           <div className="relative h-64 w-full bg-gradient-to-br from-emerald-800 via-emerald-700 to-green-900 dark:from-emerald-900 dark:via-emerald-800 dark:to-green-950 flex items-center justify-center">
              <img src="rice.jpg" className="w-full h-full object-cover" alt="Rice paddy field" />
              {/* <div className="text-center">
                <Sprout size={48} className="text-emerald-300/60 mx-auto mb-2" />
                <p className="text-emerald-300/60 text-xs font-medium">Crop Field Image</p>
              </div> */}
             <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/80 via-neutral-900/20 to-transparent"></div>
             <div className="absolute bottom-6 left-6 right-6">
               <div className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-sm uppercase tracking-wider mb-2 inline-block">{t('dashboard.main_grid.top_recommendation')}</div>
               <h2 className="text-white text-3xl font-black drop-shadow-lg">{data?.predicted_crop ?? '...'} {t('dashboard.main_grid.cultivation')}</h2>
               <p className="text-emerald-100 text-sm font-medium mt-1 flex items-center gap-1.5"><MapIcon size={14}/> {t('dashboard.main_grid.highly_viable')} {soil} {t('dashboard.main_grid.soil_profiles')}</p>
             </div>
           </div>
           
           <div className="p-6 bg-white dark:bg-slate-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 w-full md:w-auto">
                <div className="bg-neutral-50 dark:bg-slate-700/30 p-3 rounded-lg border border-neutral-100 dark:border-slate-600">
                  <p className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-widest">{t('dashboard.main_grid.crop_health')}</p>
                  <p className="text-sm font-bold mt-1 flex items-center gap-1.5" style={{ color: healthTone.color }}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: healthTone.color }}></span> {healthTone.label}
                  </p>
                </div>
                <div className="bg-neutral-50 dark:bg-slate-700/30 p-3 rounded-lg border border-neutral-100 dark:border-slate-600">
                  <p className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-widest">{t('dashboard.main_grid.growth_phase')}</p>
                  <p className="text-sm font-bold text-neutral-800 dark:text-neutral-200 mt-1">{t('dashboard.main_grid.sowing')}</p>
                </div>
                <div className="bg-neutral-50 dark:bg-slate-700/30 p-3 rounded-lg border border-neutral-100 dark:border-slate-600 col-span-2 sm:col-span-1">
                  <p className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-widest">{t('dashboard.main_grid.sowing_timeline')}</p>
                  <p className="text-sm font-bold text-neutral-800 dark:text-neutral-200 mt-1">{t('dashboard.main_grid.within_7_days')}</p>
                </div>
              </div>
              <button 
                onClick={() => window.location.href='/dashboard/crops'} 
                className="text-sm font-bold text-emerald-700 dark:text-emerald-400 border-2 border-emerald-600 dark:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 px-6 py-3 rounded-xl transition-colors w-full md:w-auto mt-2 md:mt-0"
              >
                {t('dashboard.main_grid.full_overview')}
              </button>
           </div>
        </div>

        {/* Task List */}
        <div className="lg:col-span-1 dashboard-card p-0 flex flex-col h-full bg-white dark:bg-slate-800 border-neutral-200 dark:border-slate-700">
          <div className="flex items-center justify-between p-6 border-b border-neutral-100 dark:border-slate-700">
            <h3 className="text-lg font-black text-neutral-900 dark:text-neutral-100">{t('dashboard.tasks.title')}</h3>
            <span className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400 px-3 py-1 rounded-full text-xs font-bold">4 {t('dashboard.tasks.active')}</span>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {[
              { name: `${t('dashboard.tasks.names.prepare')} ${data?.predicted_crop ?? 'Crop'} ${t('dashboard.tasks.names.sowing')}`, date: t('dashboard.tasks.dates.tomorrow'), due: `1d ${t('dashboard.tasks.due')}`, icon: <Tractor size={18} />, color: 'bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/30' },
              { name: t('dashboard.tasks.names.watering'), date: t('dashboard.tasks.dates.may_8'), due: `2d ${t('dashboard.tasks.due')}`, icon: <Droplets size={18} />, color: 'bg-blue-50 text-blue-600 border-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/30' },
              { name: t('dashboard.tasks.names.treatment'), date: t('dashboard.tasks.dates.may_10'), due: `4d ${t('dashboard.tasks.due')}`, icon: <Sprout size={18} />, color: 'bg-amber-50 text-amber-600 border-amber-100 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800/30' },
              { name: t('dashboard.tasks.names.ph_test'), date: t('dashboard.tasks.dates.may_15'), due: `9d ${t('dashboard.tasks.due')}`, icon: <Leaf size={18} />, color: 'bg-purple-50 text-purple-600 border-purple-100 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800/30' },
            ].map((task, i) => (
              <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white dark:bg-slate-800 hover:bg-neutral-50 dark:hover:bg-slate-700/50 rounded-xl border border-neutral-100 dark:border-slate-700 shadow-sm cursor-pointer transition-colors group">
                <div className="flex items-center gap-3">
                   <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${task.color}`}>
                     {task.icon}
                   </div>
                   <div>
                     <p className="text-neutral-900 dark:text-neutral-200 font-bold text-sm group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">{task.name}</p>
                     <p className="text-neutral-500 dark:text-neutral-400 font-medium text-xs mt-0.5">{task.date}</p>
                   </div>
                </div>
                <div className="mt-3 sm:mt-0 text-right">
                  <span className="text-[10px] uppercase font-bold text-neutral-500 dark:text-neutral-400">
                    {task.due}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="p-4 border-t border-neutral-100 dark:border-slate-700">
             <button className="w-full py-2 text-sm font-bold text-neutral-500 hover:text-emerald-600 transition-colors">{t('dashboard.tasks.view_all')}</button>
          </div>
        </div>

      </div>

      {/* 4. FORECAST ROW */}
      <div className="pt-2">
        <div className="dashboard-card p-6 border-t-4 border-t-blue-400 flex flex-col w-full shadow-inner">
           <div className="flex items-center gap-2 mb-4">
               <CloudSun className="text-blue-500" size={20} />
               <h3 className="text-lg font-black text-neutral-900 dark:text-neutral-100">{t('dashboard.forecast.title', '7-Day Rain Forecast')}</h3>
           </div>
           
           {data?.weather?.forecast && data.weather.forecast.length > 0 ? (
             <div className="flex items-end justify-between h-48 mt-4 gap-2 relative">
               {data.weather.forecast.map((day, idx) => {
                  const dateObj = new Date(day.date);
                  const dayName = dateObj.toLocaleDateString(i18n.language || 'en', { weekday: 'short' });
                  const maxRain = Math.max(...data.weather.forecast.map(d => d.rain || 0), 10);
                  const heightPct = Math.max(((day.rain || 0) / maxRain) * 100, 2);
                  
                  return (
                    <div key={idx} className="flex flex-col items-center flex-1 group w-full relative">
                      <div className="w-full flex justify-center h-40 items-end relative">
                         {/* Tooltip */}
                         <div className="opacity-0 group-hover:opacity-100 absolute bottom-full mb-2 bg-slate-800 text-white text-xs p-2 rounded-lg pointer-events-none transition-opacity whitespace-nowrap z-10 shadow-lg">
                           <p className="font-bold border-b border-slate-600 pb-1 mb-1">{dateObj.toLocaleDateString()}</p>
                           <p>Rain: <span className="font-bold text-blue-300">{day.rain} mm</span></p>
                           <p>High: <span className="font-bold text-orange-300">{day.max_temp}Â°C</span></p>
                           <p>Low: <span className="font-bold text-blue-200">{day.min_temp}Â°C</span></p>
                         </div>
                         {/* Bar */}
                         <div 
                           className="w-full max-w-[40px] bg-blue-300 dark:bg-blue-600 rounded-t-md transition-all duration-300 group-hover:bg-blue-500 dark:group-hover:bg-blue-400 shadow-sm"
                           style={{ height: `${heightPct}%` }}
                         ></div>
                      </div>
                      <p className="text-xs font-bold text-neutral-600 dark:text-neutral-400 mt-2">{dayName}</p>
                    </div>
                  );
               })}
             </div>
           ) : (
             <div className="h-48 flex items-center justify-center">
               <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Forecast data not available.</p>
             </div>
           )}
        </div>
      </div>

      {/* 5. LOWER METRICS ROW */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
        
        {/* Expected Yield & Profit Preview Card */}
        <div className="dashboard-card p-6 border-l-4 border-l-amber-400">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-black text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
               <TrendingUp className="text-amber-500" size={20}/> {t('dashboard.lower.profit_title')}
            </h3>
            <button className="p-1 rounded-md text-neutral-400 hover:bg-neutral-100 dark:hover:bg-slate-700 transition-colors"><ChevronRight size={20}/></button>
          </div>
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-6">{t('dashboard.lower.based_on')} {data?.predicted_crop ?? 'crop'} {t('dashboard.lower.market_futures')}</p>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-xl border border-emerald-100 dark:border-emerald-800/30">
               <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold mb-1 uppercase tracking-widest">{t('dashboard.lower.est_revenue')}</p>
               <p className="text-2xl font-black text-emerald-900 dark:text-emerald-300">{"\u20B9"}45,000</p>
               <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-500 mt-2 bg-emerald-100 dark:bg-emerald-800/50 inline-block px-2 py-0.5 rounded-sm">+12% {t('dashboard.lower.ly')}</p>
            </div>
            <div className="p-4 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-100 dark:border-amber-800/30">
               <p className="text-[10px] text-amber-700 dark:text-amber-400 font-bold mb-1 uppercase tracking-widest">{t('dashboard.lower.maint_cost')}</p>
               <p className="text-2xl font-black text-amber-900 dark:text-amber-300">{"\u20B9"}12,400</p>
               <p className="text-[10px] font-bold text-amber-700 dark:text-amber-500 mt-2 bg-amber-100 dark:bg-amber-800/50 inline-block px-2 py-0.5 rounded-sm">{t('dashboard.lower.labor')}</p>
            </div>
          </div>
        </div>

        {/* Wiki/Guide Snapshot */}
        <div className="dashboard-card p-6 border-l-4 border-l-blue-400">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-black text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
               <BookOpen className="text-blue-500" size={20}/> {t('dashboard.lower.guide_title')}
            </h3>
            <button className="p-1 rounded-md text-neutral-400 hover:bg-neutral-100 dark:hover:bg-slate-700 transition-colors"><ChevronRight size={20}/></button>
          </div>
          
          <div className="flex gap-4 p-4 rounded-xl bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 transition-colors cursor-pointer group">
             <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0 border border-neutral-200 dark:border-slate-700 bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-900/30 dark:to-amber-800/30 flex items-center justify-center">
               {/* Replace this div with an <img> tag for soil tips image */}
               <Leaf size={24} className="text-amber-500/60 dark:text-amber-400/50" />
             </div>
             <div>
                <h4 className="text-sm font-black text-neutral-900 dark:text-neutral-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{t('dashboard.lower.best_practices')} {soil}</h4>
                <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400 mt-1.5 leading-relaxed line-clamp-2">{t('dashboard.lower.guide_desc')}</p>
                <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 mt-2 inline-block uppercase tracking-wider">{t('dashboard.lower.read_article')} &rarr;</span>
             </div>
          </div>
        </div>

      </div>

    </div>
  );
}


